import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, CalendarDays, Mail, RotateCcw, Save, Send, Undo2,
} from 'lucide-react';
import { Actions, Button, Card, CardTitle, Empty, Message } from '../admin/ui';
import * as Api from './EmailAdminApi';
import {
  backendMessage, EmailPreview, EmailViewSwitch, PREVIEW_DELAY_MS, TemplateFields, TemplateProblems,
} from './TemplateParts';
import { saveProblems, recipientLabel, templateName, templateWhen } from './emailAdminModel';

// One email template, edited on the E-Mails screen. Generalised from the old invite-reminder card in
// programAdmin, with the owner's layout rule: the editor and the preview are two views of one
// column, never columns beside each other, so a long template cannot leave a short preview next to a
// lot of empty space.
//
// The text is saved and checked by the backend (email.EmailTemplatesController). The preview is the
// backend's rendering of the unsaved text with a made-up person and event and the dummy token
// PREVIEW; it is refreshed after a pause in typing, never on every keystroke, and shows the problems
// a save would report. Save stays off while such a problem exists.
const EmailTemplateEditor = ({ templateKey, onBack, onDirtyChange, onShowSent }) => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);

  const [saved, setSaved] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [view, setView] = useState('edit');
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [message, setMessage] = useState(null);
  const [saveProblemsState, setSaveProblemsState] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const requestSeq = useRef(0);

  const apply = useCallback((data) => {
    setSaved(data);
    setSubject(data.subject);
    setHtml(data.html);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setSaved(null);
    setLoadError('');
    Api.fetchTemplate(templateKey)
      .then((data) => { if (!cancelled) apply(data); })
      .catch((err) => {
        if (!cancelled) setLoadError(backendMessage(err, t('emailAdmin.template.loadFailed', 'Could not load the template.')));
      });
    return () => { cancelled = true; };
  }, [templateKey, apply]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = Boolean(saved) && (subject !== saved.subject || html !== saved.html);
  const contentKey = JSON.stringify([subject, html]);

  // A refused save's list belongs to the text that was refused; the next edit starts clean.
  useEffect(() => { setSaveProblemsState([]); }, [contentKey]);

  // Tell the screen, so it can ask before a tab switch, a link or the page takes the editor away
  // (the screen owns those guards once, for the editor and the schedule cards alike).
  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);

  const runPreview = useCallback(async (seq) => {
    setPreviewing(true);
    try {
      const data = await Api.previewTemplate(templateKey, subject, html);
      if (seq === requestSeq.current) {
        setPreview({ ...data, of: JSON.stringify([subject, html]) });
        setPreviewError('');
      }
    } catch (err) {
      if (seq === requestSeq.current) {
        setPreviewError(backendMessage(err, t('emailAdmin.template.previewFailed', 'Could not prepare the preview.')));
      }
    } finally {
      if (seq === requestSeq.current) setPreviewing(false);
    }
  }, [templateKey, subject, html]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced: a pause in typing, not every keystroke. The first preview comes as soon as a template
  // is loaded, so the problems a save would report are known before the admin touches anything.
  useEffect(() => {
    if (!saved) return undefined;
    const seq = ++requestSeq.current;
    const timer = setTimeout(() => runPreview(seq), PREVIEW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [saved, contentKey, runPreview]);

  const placeholders = useMemo(() => saved?.placeholders || [], [saved]);
  const previewCurrent = preview?.of === contentKey && !previewing;
  const problems = previewCurrent ? (preview.problems || []) : [];
  const canSave = dirty && !saving && previewCurrent && problems.length === 0;

  const save = async () => {
    setSaving(true);
    setMessage(null);
    setSaveProblemsState([]);
    try {
      const data = await Api.saveTemplate(templateKey, subject, html);
      apply(data);
      setMessage({ text: t('emailAdmin.template.saved', 'Template saved. Every email of this type from now on uses it.') });
    } catch (err) {
      const refused = saveProblems(err);
      if (refused) {
        setSaveProblemsState(refused);
        setPreview({ ...(preview || {}), problems: refused, of: contentKey });
      } else {
        setMessage({ error: true, text: backendMessage(err, t('emailAdmin.template.saveFailed', 'Could not save the template.')) });
      }
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setConfirmReset(false);
    setMessage(null);
    setSaveProblemsState([]);
    try {
      const data = await Api.resetTemplate(templateKey);
      apply(data);
      setMessage({ text: t('emailAdmin.template.resetDone', 'The built-in default template is in use again.') });
    } catch (err) {
      setMessage({ error: true, text: backendMessage(err, t('emailAdmin.template.saveFailed', 'Could not save the template.')) });
    }
  };

  const loadDefault = () => {
    setConfirmReset(false);
    setSubject(saved.defaultSubject);
    setHtml(saved.defaultHtml);
  };

  const confirmLeave = () => !dirty
    || window.confirm(t('emailAdmin.template.leaveConfirm', 'You have unsaved changes. Leave and lose them?'));

  const back = () => { if (confirmLeave()) onBack(); };
  const showSent = () => { if (confirmLeave()) onShowSent(saved.mailType); };

  if (loadError) {
    return (
      <Card>
        <Message error role="alert">{loadError}</Message>
        <Button type="button" secondary onClick={onBack}>
          <ArrowLeft size={15} /><FormattedMessage id="emailAdmin.template.back" defaultMessage="Back to all templates" />
        </Button>
      </Card>
    );
  }
  if (!saved) {
    return <Card><Empty><FormattedMessage id="emailAdmin.template.loading" defaultMessage="Loading template…" /></Empty></Card>;
  }

  const name = templateName(t, saved);
  const status = saved.isDefault
    ? <FormattedMessage id="emailAdmin.template.isDefault" defaultMessage="Built-in default." />
    : (
      <FormattedMessage id="emailAdmin.template.lastSaved"
        defaultMessage="Edited {date}{by, select, none {} other { by {by}}}."
        values={{
          date: saved.updatedAt
            ? intl.formatDate(new Date(saved.updatedAt), { dateStyle: 'medium', timeStyle: 'short' })
            : '',
          by: saved.updatedBy || 'none',
        }} />
    );

  return (
    <Card>
      <div style={{ display: 'flex', gap: '.75rem', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <Button type="button" secondary onClick={back} style={{ marginBottom: '.75rem' }}>
            <ArrowLeft size={15} /><FormattedMessage id="emailAdmin.template.back" defaultMessage="Back to all templates" />
          </Button>
          <CardTitle style={{ margin: 0, overflowWrap: 'break-word' }}>
            <Mail size={17} style={{ verticalAlign: '-3px', marginRight: '.35rem' }} />
            {name}
          </CardTitle>
          <p style={{ margin: '.35rem 0 0', fontSize: '.82rem', color: '#475569' }}>
            <FormattedMessage id="emailAdmin.template.to" defaultMessage="To: {recipient}"
              values={{ recipient: recipientLabel(t, saved.recipient) }} />
            {' · '}{templateWhen(t, saved)}
          </p>
          <p style={{ margin: '.25rem 0 0', fontSize: '.78rem', color: '#64748b' }}>{status}</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '.5rem' }}>
          <Button type="button" secondary onClick={showSent}>
            <Send size={15} /><FormattedMessage id="emailAdmin.template.showSent" defaultMessage="Show sent emails of this type" />
          </Button>
          {saved.schedule?.kind && (
            <Link to={`/admin/emails?tab=schedules#schedule-${saved.schedule.kind}`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '.35rem', color: '#0369a1', fontSize: '.8rem', fontWeight: 700, textDecoration: 'none' }}>
              <CalendarDays size={15} />
              <FormattedMessage id="emailAdmin.template.showInSchedules" defaultMessage="Show in schedules" />
            </Link>
          )}
        </div>
      </div>

      {saved.directAction && (
        <div style={{ marginTop: '.9rem', fontSize: '.8rem', color: '#075985' }}>
          <FormattedMessage id="emailAdmin.template.directActionHint"
            defaultMessage="This email can also be edited once when it is sent." />
        </div>
      )}

      {saved.accountWarning && (
        <Message warn role="alert" style={{ marginTop: '1rem' }}>
          <strong><AlertTriangle size={15} style={{ verticalAlign: '-2px', marginRight: '.3rem' }} />
            <FormattedMessage id="emailAdmin.template.accountWarningTitle" defaultMessage="This email can lock people out." />
          </strong>
          <div style={{ marginTop: '.3rem' }}>
            <FormattedMessage id="emailAdmin.template.accountWarning"
              defaultMessage="A broken template can stop people from logging in, signing up or confirming their address. Keep the required link below." />
          </div>
        </Message>
      )}

      {(saved.required || []).length > 0 && (
        <div style={{ marginTop: '1rem', padding: '.7rem .8rem', background: '#f8fafc', borderLeft: '3px solid #47ABDB', fontSize: '.82rem', color: '#334155' }}>
          <strong><FormattedMessage id="emailAdmin.template.mustKeepTitle" defaultMessage="Must stay in the email:" /></strong>
          <ul style={{ margin: '.35rem 0 0', paddingLeft: '1.1rem' }}>
            {saved.required.map((required) => (
              <li key={required.problem}>
                {required.anyOf.map((key) => <code key={key} style={{ marginRight: '.35rem' }}>{`{{${key}}}`}</code>)}
                <span style={{ color: '#64748b', display: 'block', fontSize: '.76rem' }}>{required.problem}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <EmailViewSwitch view={view} onChange={setView} />

      {view === 'edit' ? (
        <div>
          <p style={{ margin: '0 0 1rem', fontSize: '.8rem', color: '#64748b' }}>
            <FormattedMessage id="emailAdmin.template.intro"
              defaultMessage="Write the email as HTML; anything in double curly braces is filled in when it is sent. Click a placeholder below to insert it where your cursor is. The email is the whole message – there is no shared layout." />
          </p>
          <TemplateFields subject={subject} html={html} onChangeSubject={setSubject} onChangeHtml={setHtml}
            placeholders={placeholders} />
        </div>
      ) : (
        <div>
          <p style={{ margin: '0 0 .75rem', fontSize: '.8rem', color: '#64748b' }}>
            <strong><FormattedMessage id="emailAdmin.template.sampleTitle" defaultMessage="Sample data." /></strong>{' '}
            <FormattedMessage id="emailAdmin.template.sampleHint"
              defaultMessage="The preview is filled with a made-up person and event, never real people; its links carry the dummy token PREVIEW." />
          </p>
          {previewError && <Message error role="alert">{previewError}</Message>}
          <EmailPreview subject={preview?.subject} html={preview?.html} loading={previewing}
            refresh={() => { const seq = ++requestSeq.current; runPreview(seq); }} />
        </div>
      )}

      <div style={{ marginTop: '1rem' }}>
        <TemplateProblems problems={saveProblemsState.length > 0 ? saveProblemsState : problems}
          titleId="emailAdmin.template.problems" titleDefault="This can't be saved yet:" />
        {message && <Message error={message.error}>{message.text}</Message>}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '.82rem', color: dirty ? '#92400e' : '#64748b', fontWeight: dirty ? 700 : 400 }}>
            {dirty
              ? <FormattedMessage id="emailAdmin.template.unsaved" defaultMessage="Unsaved changes – check the preview, then save." />
              : status}
          </span>
          <Actions style={{ margin: 0 }}>
            {dirty && (
              <Button type="button" secondary onClick={() => { apply(saved); setMessage(null); setSaveProblemsState([]); }}>
                <Undo2 size={15} /><FormattedMessage id="emailAdmin.template.undo" defaultMessage="Discard changes" />
              </Button>
            )}
            {!saved.isDefault && (
              <Button type="button" secondary onClick={() => setConfirmReset(true)}>
                <RotateCcw size={15} /><FormattedMessage id="emailAdmin.template.reset" defaultMessage="Reset to default" />
              </Button>
            )}
            <Button type="button" disabled={!canSave} onClick={save}
              title={dirty && !previewCurrent ? t('emailAdmin.template.waitPreview', 'Wait for the preview to show your latest changes') : undefined}>
              <Save size={15} />{saving
                ? <FormattedMessage id="emailAdmin.template.saving" defaultMessage="Saving…" />
                : <FormattedMessage id="emailAdmin.template.save" defaultMessage="Save template" />}
            </Button>
          </Actions>
        </div>
        {confirmReset && (
          <Message warn style={{ marginTop: '.75rem', marginBottom: 0 }}>
            <FormattedMessage id="emailAdmin.template.resetConfirm"
              defaultMessage="Replace the saved template with the built-in default? The saved version is deleted. (To compare first, load the default into the editor – nothing is saved until you click Save.)" />
            <Actions style={{ marginTop: '.5rem' }}>
              <Button type="button" secondary onClick={() => setConfirmReset(false)}>
                <FormattedMessage id="emailAdmin.template.cancel" defaultMessage="Cancel" />
              </Button>
              <Button type="button" secondary onClick={loadDefault}>
                <FormattedMessage id="emailAdmin.template.loadDefault" defaultMessage="Load default into editor" />
              </Button>
              <Button type="button" danger onClick={reset}>
                <RotateCcw size={15} /><FormattedMessage id="emailAdmin.template.resetNow" defaultMessage="Reset now" />
              </Button>
            </Actions>
          </Message>
        )}
      </div>
    </Card>
  );
};

export default EmailTemplateEditor;
